import {
  Component,
  Input,
  Output,
  EventEmitter,
  computed,
  signal,
  inject,
  forwardRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgClass, NgIf } from '@angular/common';

export type AppInputType =
  | 'text'
  | 'email'
  | 'password'
  | 'number'
  | 'tel'
  | 'url'
  | 'search';
export type AppInputSize = 'sm' | 'md' | 'lg';

/**
 * AppInput - Componente de input padronizado com validação
 *
 * @example
 * ```html
 * <app-input
 *   label="Nome completo"
 *   placeholder="Digite seu nome"
 *   [control]="nameControl"
 *   type="text"
 *   size="md"
 *   [required]="true"
 *   helperText="Informe seu nome completo"
 * ></app-input>
 * ```
 */
@Component({
  selector: 'app-input',
  standalone: true,
  imports: [NgClass, ReactiveFormsModule],
  template: `
    <div class="input-wrapper" [ngClass]="wrapperClasses()">
      @if (label) {
      <label
        [for]="inputId"
        class="input-label"
        [ngClass]="{ required: required() }"
      >
        {{ label }}
        @if (required()) {
        <span class="text-accent-500 ml-1" aria-label="obrigatório">*</span>
        }
      </label>
      }

      <div class="input-container" [ngClass]="containerClasses()">
        @if (iconLeft) {
        <div class="input-icon input-icon-left">
          <i [ngClass]="'icon-' + iconLeft" aria-hidden="true"></i>
        </div>
        }

        <input
          [id]="inputId"
          [type]="type"
          [placeholder]="placeholder"
          [formControl]="control"
          [disabled]="disabled()"
          [readonly]="readonly()"
          [ngClass]="inputClasses()"
          [attr.aria-invalid]="hasError()"
          [attr.aria-describedby]="getAriaDescribedBy()"
          (focus)="onFocus()"
          (blur)="onBlur()"
          (input)="onInput($event)"
        />

        @if (iconRight) {
        <div class="input-icon input-icon-right">
          <i [ngClass]="'icon-' + iconRight" aria-hidden="true"></i>
        </div>
        } @if (clearable() && value() && !disabled()) {
        <button
          type="button"
          class="input-clear"
          (click)="clearValue()"
          aria-label="Limpar campo"
        >
          <i class="icon-x" aria-hidden="true"></i>
        </button>
        }
      </div>

      @if (helperText && !hasError()) {
      <p class="input-helper" [id]="helperId">
        {{ helperText }}
      </p>
      } @if (hasError()) {
      <p class="input-error" [id]="errorId" role="alert">
        {{ getErrorMessage() }}
      </p>
      }
    </div>
  `,
  styles: [
    `
      .input-wrapper {
        @apply block w-full;
      }

      .input-label {
        @apply block text-sm font-medium text-neutral-700 mb-1;
      }

      .input-label.required {
        @apply text-neutral-800;
      }

      .input-container {
        @apply relative;
      }

      .input-container.has-error {
        @apply ring-1 ring-accent-500 rounded-lg;
      }

      input {
        @apply block w-full rounded-lg border-0 px-3 py-2
        text-neutral-900 shadow-soft ring-1 ring-inset ring-neutral-300
        placeholder:text-neutral-400
        focus:ring-2 focus:ring-inset focus:ring-primary-500
        disabled:cursor-not-allowed disabled:bg-neutral-50 disabled:text-neutral-500
        disabled:cursor-not-allowed disabled:bg-neutral-50;
      }

      input.has-icon-left {
        @apply pl-10;
      }

      input.has-icon-right {
        @apply pr-10;
      }

      input.has-clear {
        @apply pr-10;
      }

      /* Sizes */
      .size-sm input {
        @apply px-2.5 py-1.5 text-sm;
      }

      .size-md input {
        @apply px-3 py-2 text-base;
      }

      .size-lg input {
        @apply px-4 py-3 text-lg;
      }

      .input-icon {
        @apply absolute inset-y-0 flex items-center;
      }

      .input-icon-left {
        @apply left-0 pl-3;
      }

      .input-icon-right {
        @apply right-0 pr-3;
      }

      .input-clear {
        @apply absolute inset-y-0 right-0 flex items-center pr-3
        text-neutral-400 hover:text-neutral-600
        focus:outline-none focus:text-neutral-600;
      }

      .input-helper {
        @apply mt-1 text-sm text-neutral-600;
      }

      .input-error {
        @apply mt-1 text-sm text-accent-500 font-medium;
      }

      /* Estados de erro */
      .has-error input {
        @apply ring-accent-500 focus:ring-accent-500;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppInputComponent),
      multi: true,
    },
  ],
})
export class AppInputComponent implements ControlValueAccessor {
  // Signals para estado interno
  private _value = signal('');
  private _focused = signal(false);
  private _touched = signal(false);

  // Props configuráveis
  size = signal<AppInputSize>('md');
  disabled = signal(false);
  readonly = signal(false);
  clearable = signal(false);
  required = signal(false);

  @Input() label?: string;
  @Input() placeholder = '';
  @Input() type: AppInputType = 'text';
  @Input() iconLeft?: string;
  @Input() iconRight?: string;
  @Input() helperText?: string;
  @Input() control!: FormControl;

  // Enhanced inputs
  @Input({ required: false }) set inputSize(value: AppInputSize) {
    this.size.set(value);
  }

  @Input({ required: false }) set isDisabled(value: boolean) {
    this.disabled.set(value);
  }

  @Input({ required: false }) set isReadonly(value: boolean) {
    this.readonly.set(value);
  }

  @Input({ required: false }) set isClearable(value: boolean) {
    this.clearable.set(value);
  }

  @Input({ required: false }) set isRequired(value: boolean) {
    this.required.set(value);
  }

  // Outputs
  @Output() focused = new EventEmitter<FocusEvent>();
  @Output() blurred = new EventEmitter<FocusEvent>();
  @Output() valueChanged = new EventEmitter<string>();

  // IDs únicos para acessibilidade
  inputId = `app-input-${Math.random().toString(36).substr(2, 9)}`;
  helperId = `${this.inputId}-helper`;
  errorId = `${this.inputId}-error`;

  // Computed values
  value = computed(() => this._value());
  isFocused = computed(() => this._focused());
  touched = computed(() => this._touched());

  hasError = computed(() => {
    return this.control?.invalid && this.control?.touched;
  });

  wrapperClasses = computed(() => {
    return {
      [`size-${this.size()}`]: true,
      'has-error': this.hasError(),
    };
  });

  containerClasses = computed(() => {
    return {
      'has-error': this.hasError(),
    };
  });

  inputClasses = computed(() => {
    return {
      'has-icon-left': !!this.iconLeft,
      'has-icon-right': !!this.iconRight,
      'has-clear': this.clearable() && this.value(),
      'has-error': this.hasError(),
    };
  });

  // ControlValueAccessor implementation
  private onChange = (value: string) => {};
  private onTouched = () => {};

  writeValue(value: string): void {
    this._value.set(value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // Event handlers
  onFocus(): void {
    this._focused.set(true);
    this.focused.emit();
  }

  onBlur(): void {
    this._focused.set(false);
    this._touched.set(true);
    this.onTouched();
    this.blurred.emit();
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this._value.set(value);
    this.onChange(value);
    this.valueChanged.emit(value);
  }

  clearValue(): void {
    this._value.set('');
    this.onChange('');
    this.valueChanged.emit('');
  }

  getErrorMessage(): string {
    if (!this.control?.errors) return '';

    const errors = this.control.errors;

    if (errors['required']) return 'Este campo é obrigatório';
    if (errors['email']) return 'E-mail inválido';
    if (errors['minlength'])
      return `Mínimo de ${errors['minlength'].requiredLength} caracteres`;
    if (errors['maxlength'])
      return `Máximo de ${errors['maxlength'].requiredLength} caracteres`;
    if (errors['pattern']) return 'Formato inválido';
    if (errors['min']) return `Valor mínimo: ${errors['min'].min}`;
    if (errors['max']) return `Valor máximo: ${errors['max'].max}`;

    return 'Campo inválido';
  }

  getAriaDescribedBy(): string {
    const ids: string[] = [];

    if (this.helperText && !this.hasError()) {
      ids.push(this.helperId);
    }

    if (this.hasError()) {
      ids.push(this.errorId);
    }

    return ids.join(' ');
  }
}
